import {addDays, format, isSameDay, startOfToday} from 'date-fns'

export type AssignmentStripAssignment = {
    id: string
    jobId: string
    jobName: string
    startDate: string
    startTime: string | null
    endDate: string | null
    endTime: string | null
}

export type AssignmentStripDayStatus = 'unassigned' | 'bounded' | 'open_ended'

export type AssignmentStripDay = {
    date: Date
    dateKey: string
    isReferenceDay: boolean
    status: AssignmentStripDayStatus
    assignments: AssignmentStripAssignment[]
}

export type AssignmentStripDateRangeOptions = {
    daysBefore?: number
    daysAfter?: number
}

export const DEFAULT_ASSIGNMENT_STRIP_DATE_RANGE: AssignmentStripDateRangeOptions = {
    daysBefore: 7,
    daysAfter: 7,
}

/** Job assignment peek: 2 days before start, then 5 calendar days from start (inclusive). */
export const JOB_PEEK_ASSIGNMENT_STRIP_DATE_RANGE: AssignmentStripDateRangeOptions = {
    daysBefore: 2,
    daysAfter: 4,
}

/** Fleet type page: today through the next 4 days (5 calendar days inclusive). */
export const FORWARD_5_DAY_ASSIGNMENT_STRIP_DATE_RANGE: AssignmentStripDateRangeOptions = {
    daysBefore: 0,
    daysAfter: 4,
}

/** Dispatch typeahead peek: yesterday through five days after today (7 days). */
export const DISPATCH_TYPEAHEAD_ASSIGNMENT_STRIP_DATE_RANGE: AssignmentStripDateRangeOptions = {
    daysBefore: 1,
    daysAfter: 5,
}

export function getAssignmentStripDateRange(
    referenceDate: Date = startOfToday(),
    options: AssignmentStripDateRangeOptions = DEFAULT_ASSIGNMENT_STRIP_DATE_RANGE,
) {
    const daysBefore = options.daysBefore ?? 7
    const daysAfter = options.daysAfter ?? 7

    return {
        dateFrom: addDays(referenceDate, -daysBefore),
        dateTo: addDays(referenceDate, daysAfter),
        referenceDate,
    }
}

export function formatAssignmentStripDateRange(
    referenceDate: Date = startOfToday(),
    options: AssignmentStripDateRangeOptions = DEFAULT_ASSIGNMENT_STRIP_DATE_RANGE,
) {
    const {
        dateFrom,
        dateTo,
        referenceDate: resolvedReferenceDate,
    } = getAssignmentStripDateRange(referenceDate, options)

    return {
        dateFrom: format(dateFrom, 'yyyy-MM-dd'),
        dateTo: format(dateTo, 'yyyy-MM-dd'),
        referenceDate: resolvedReferenceDate,
    }
}

export function assignmentOverlapsDay(assignment: AssignmentStripAssignment, dateKey: string) {
    if (dateKey < assignment.startDate) {
        return false
    }
    if (assignment.endDate == null) {
        return true
    }
    return dateKey <= assignment.endDate
}

export function getAssignmentStripDayStatus(
    assignments: AssignmentStripAssignment[],
): AssignmentStripDayStatus {
    if (assignments.length === 0) {
        return 'unassigned'
    }
    if (assignments.some((assignment) => assignment.endDate == null)) {
        return 'open_ended'
    }
    return 'bounded'
}

export function buildAssignmentStripDays(
    assignments: AssignmentStripAssignment[],
    referenceDate: Date = startOfToday(),
    dateRange: AssignmentStripDateRangeOptions = DEFAULT_ASSIGNMENT_STRIP_DATE_RANGE,
): AssignmentStripDay[] {
    const {dateFrom, dateTo} = getAssignmentStripDateRange(referenceDate, dateRange)
    const days: AssignmentStripDay[] = []

    for (let cursor = dateFrom; cursor <= dateTo; cursor = addDays(cursor, 1)) {
        const dateKey = format(cursor, 'yyyy-MM-dd')
        const dayAssignments = assignments.filter((assignment) =>
            assignmentOverlapsDay(assignment, dateKey),
        )
        days.push({
            date: cursor,
            dateKey,
            isReferenceDay: isSameDay(cursor, referenceDate),
            status: getAssignmentStripDayStatus(dayAssignments),
            assignments: dayAssignments,
        })
    }

    return days
}
